import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { PackageOpen, ShieldCheck, Search, Truck, MapPin, X } from "lucide-react";
import { SearchableSelect } from "@/components/SearchableSelect";
import LocationPicker from "@/components/LocationPicker";
import { toast } from "sonner";

const VEHICLE_TYPES = [
  "Fark Etmez", "Tekli Oto Kurtarıcı", "Çoklu Araç Taşıyıcı", "Lowbed",
  "Ağır Ticari Taşıma", "İş Makinesi Taşıma", "Özel / Diğer",
];

const CATS = ["Otomobil", "SUV / Pickup", "Hafif ticari", "Kamyon", "Tır / Çekici", "Otobüs / Minibüs", "İş makinesi", "Traktör", "Forklift", "Diğer"];
const CONDS = ["Çalışıyor", "Çalışmıyor", "Hasarlı"];

const EMPTY_REQ = {
  pickup_district: "", pickup_address: "", pickup_lat: null, pickup_lng: null,
  delivery_district: "", delivery_address: "", delivery_lat: null, delivery_lng: null,
  transport_category: "Otomobil", quantity: 1, running_condition: "Çalışıyor",
  make_model: "", description: "", deadline_at: "",
};

export default function EmptyReturnSearch() {
  const nav = useNavigate();
  const [provinces, setProvinces] = useState([]);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [vType, setVType] = useState("Fark Etmez");
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);

  // Teklif İste modal state
  const [target, setTarget] = useState(null); // { empty_return, provider_id, pickup_province, delivery_province }
  const [reqForm, setReqForm] = useState(EMPTY_REQ);
  const [puDist, setPuDist] = useState([]);
  const [deDist, setDeDist] = useState([]);

  useEffect(() => { api.get("/locations/provinces").then(({ data }) => setProvinces(data)); }, []);
  useEffect(() => {
    if (target?.pickup_province) api.get("/locations/districts", { params: { province: target.pickup_province } }).then(({ data }) => setPuDist(data));
    else setPuDist([]);
  }, [target?.pickup_province]);
  useEffect(() => {
    if (target?.delivery_province) api.get("/locations/districts", { params: { province: target.delivery_province } }).then(({ data }) => setDeDist(data));
    else setDeDist([]);
  }, [target?.delivery_province]);

  const search = async (e) => {
    e?.preventDefault();
    if (!from || !to) return toast.error("Nereden ve Nereye il seçin");
    setBusy(true);
    try {
      const { data } = await api.get("/transport/empty-returns", { params: { from_province: from, to_province: to } });
      let filtered = data;
      if (dateFrom || dateTo) {
        const fromMs = dateFrom ? new Date(dateFrom).getTime() : -Infinity;
        const toMs = dateTo ? new Date(dateTo).getTime() + 24 * 3600 * 1000 : Infinity;
        filtered = filtered.filter((r) => {
          const t = new Date(r.departure_at).getTime();
          return t >= fromMs && t <= toMs;
        });
      }
      setResults(filtered);
    } catch (err) { toast.error("Arama başarısız"); }
    finally { setBusy(false); }
  };

  const openOfferReq = (er) => {
    setTarget({
      empty_return_id: er.id,
      provider_id: er.provider_id,
      pickup_province: er.from_province,
      delivery_province: er.to_province,
      departure_at: er.departure_at,
    });
    setReqForm({ ...EMPTY_REQ, deadline_at: er.departure_at ? er.departure_at.slice(0, 16) : "" });
  };
  const closeOfferReq = () => { setTarget(null); setReqForm(EMPTY_REQ); };

  const submitOfferReq = async (e) => {
    e.preventDefault();
    if (!reqForm.pickup_district || !reqForm.delivery_district) return toast.error("Alım ve teslim ilçe seçin");
    if (!reqForm.deadline_at) return toast.error("Tarih/saat seçin");
    try {
      await api.post("/transport/requests", {
        ...reqForm,
        pickup_province: target.pickup_province,
        delivery_province: target.delivery_province,
        deadline_at: new Date(reqForm.deadline_at).toISOString(),
        quantity: Number(reqForm.quantity) || 1,
        pickup_lat: reqForm.pickup_lat || null,
        pickup_lng: reqForm.pickup_lng || null,
        delivery_lat: reqForm.delivery_lat || null,
        delivery_lng: reqForm.delivery_lng || null,
        target_provider_id: target.provider_id,
        target_empty_return_id: target.empty_return_id,
      });
      toast.success("Teklif isteği gönderildi. Sağlayıcı yanıt verdiğinde bildirim alacaksınız.");
      closeOfferReq();
      nav("/taleplerim");
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-6">
      <div className="relative overflow-hidden rounded-2xl mb-4 bg-[hsl(var(--brand-black))] p-5 sm:p-6">
        <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #FFC107 0px, transparent 120px), radial-gradient(circle at 85% 80%, #FFC107 0px, transparent 160px)" }} />
        <div className="relative flex items-start gap-3">
          <div className="w-12 h-12 rounded-2xl bg-primary/20 border border-primary/40 flex items-center justify-center shrink-0">
            <PackageOpen className="w-6 h-6 text-primary" />
          </div>
          <div className="text-white">
            <div className="flex items-center gap-1 text-primary text-[10px] font-bold tracking-widest">
              <ShieldCheck className="w-3 h-3" /> YOL HATTI · BOŞ DÖNÜŞ
            </div>
            <h1 className="text-2xl font-extrabold tracking-tight">Boş Dönüş Ara</h1>
            <p className="text-xs text-white/70 mt-1">Belirlediğiniz güzergâhta müsait taşıyıcıları bulun ve doğrudan teklif isteyin.</p>
          </div>
        </div>
      </div>

      <form onSubmit={search} className="rounded-2xl border p-4 space-y-3 mb-6" data-testid="er-search-form">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><Label>Nereden İl</Label><SearchableSelect testid="er-search-from" value={from} onChange={setFrom} options={provinces} placeholder="İl seçin" /></div>
          <div><Label>Nereye İl</Label><SearchableSelect testid="er-search-to" value={to} onChange={setTo} options={provinces} placeholder="İl seçin" /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Başlangıç Tarihi</Label><Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="er-search-dfrom" /></div>
          <div><Label>Bitiş Tarihi</Label><Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="er-search-dto" /></div>
        </div>
        <div><Label>Taşıma / Araç Türü</Label>
          <Select value={vType} onValueChange={setVType}>
            <SelectTrigger data-testid="er-search-vtype"><SelectValue /></SelectTrigger>
            <SelectContent>{VEHICLE_TYPES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button type="submit" disabled={busy} data-testid="er-search-submit" className="w-full rounded-full bg-primary text-[hsl(var(--brand-black))] hover:bg-primary/90 font-bold">
          <Search className="w-4 h-4 mr-1" /> Boş Dönüş Ara
        </Button>
      </form>

      {results !== null && (
        <div>
          <div className="text-sm font-semibold mb-2">Sonuçlar ({results.length})</div>
          <div className="grid gap-2">
            {results.length === 0 && (
              <div className="rounded-2xl border border-dashed p-6 text-center text-sm text-muted-foreground">
                <PackageOpen className="w-8 h-8 mx-auto mb-2 opacity-50" />
                Bu güzergâh ve tarihte uygun boş dönüş yok.
              </div>
            )}
            {results.map((r) => (
              <div key={r.id} className="rounded-2xl border p-3" data-testid={`er-res-${r.id}`}>
                <div className="flex items-center justify-between flex-wrap gap-1">
                  <div className="text-sm font-semibold"><b>{r.from_province}</b> → <b>{r.to_province}</b></div>
                  <Badge className="bg-emerald-500 text-white hover:bg-emerald-500">Aktif</Badge>
                </div>
                <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><MapPin className="w-3 h-3" /> {new Date(r.departure_at).toLocaleDateString("tr-TR", { day: "2-digit", month: "long", year: "numeric" })} · {new Date(r.departure_at).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}</div>
                {r.capacity_note && <div className="text-xs mt-1">Not: {r.capacity_note}</div>}
                <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><Truck className="w-3 h-3" /> YOL HATTI onaylı taşıyıcı</div>
                <div className="mt-2 flex gap-2">
                  <Button size="sm" onClick={() => openOfferReq(r)} data-testid={`er-offer-req-${r.id}`} className="rounded-full bg-primary text-[hsl(var(--brand-black))] hover:bg-primary/90 font-bold">
                    Teklif İste
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ============= TEKLİF İSTE MODAL ============= */}
      {target && (
        <div className="fixed inset-0 bg-black/60 z-50 grid place-items-center p-3 sm:p-4">
          <form onSubmit={submitOfferReq} className="bg-card rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto" data-testid="er-request-modal">
            <div className="sticky top-0 z-10 bg-card border-b p-3 flex items-center justify-between">
              <div>
                <div className="text-xs text-muted-foreground">Hedefli Teklif İsteği</div>
                <div className="text-lg font-extrabold">{target.pickup_province} → {target.delivery_province}</div>
              </div>
              <Button type="button" variant="ghost" size="sm" onClick={closeOfferReq}><X className="w-5 h-5" /></Button>
            </div>
            <div className="p-4 space-y-2">
              <div className="rounded-xl bg-primary/10 border border-primary/40 p-2 text-xs flex items-start gap-2">
                <ShieldCheck className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                <span>Bu istek yalnızca ilgili sağlayıcıya gider — iş havuzuna düşmez. Kesin adres kabul edilen firmayla paylaşılır.</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Alım İl</Label>
                  <Input value={target.pickup_province} disabled />
                </div>
                <div>
                  <Label>Alım İlçe *</Label>
                  <Select value={reqForm.pickup_district} onValueChange={(v) => setReqForm({ ...reqForm, pickup_district: v })}>
                    <SelectTrigger data-testid="er-req-pdist"><SelectValue placeholder="İlçe seç" /></SelectTrigger>
                    <SelectContent>{puDist.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <Input placeholder="Alım açık adres (ops.)" value={reqForm.pickup_address} onChange={(e) => setReqForm({ ...reqForm, pickup_address: e.target.value })} data-testid="er-req-paddr" />
              <div>
                <Label className="text-xs">Alım Konumu (haritadan seç)</Label>
                <LocationPicker
                  testid="er-req-pu-map"
                  height={180}
                  currentContext={{ province: target.pickup_province, district: reqForm.pickup_district }}
                  value={reqForm.pickup_lat ? { lat: reqForm.pickup_lat, lng: reqForm.pickup_lng } : null}
                  onChange={({ lat, lng, district, address, road }) => setReqForm((s) => ({
                    ...s,
                    pickup_lat: lat, pickup_lng: lng,
                    pickup_district: s.pickup_district || district || "",
                    pickup_address: s.pickup_address || road || address || "",
                  }))}
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Teslim İl</Label>
                  <Input value={target.delivery_province} disabled />
                </div>
                <div>
                  <Label>Teslim İlçe *</Label>
                  <Select value={reqForm.delivery_district} onValueChange={(v) => setReqForm({ ...reqForm, delivery_district: v })}>
                    <SelectTrigger data-testid="er-req-ddist"><SelectValue placeholder="İlçe seç" /></SelectTrigger>
                    <SelectContent>{deDist.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <Input placeholder="Teslim açık adres (ops.)" value={reqForm.delivery_address} onChange={(e) => setReqForm({ ...reqForm, delivery_address: e.target.value })} data-testid="er-req-daddr" />
              <div>
                <Label className="text-xs">Teslim Konumu (haritadan seç)</Label>
                <LocationPicker
                  testid="er-req-de-map"
                  height={180}
                  currentContext={{ province: target.delivery_province, district: reqForm.delivery_district }}
                  value={reqForm.delivery_lat ? { lat: reqForm.delivery_lat, lng: reqForm.delivery_lng } : null}
                  onChange={({ lat, lng, district, address, road }) => setReqForm((s) => ({
                    ...s,
                    delivery_lat: lat, delivery_lng: lng,
                    delivery_district: s.delivery_district || district || "",
                    delivery_address: s.delivery_address || road || address || "",
                  }))}
                />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div><Label>Kategori</Label>
                  <Select value={reqForm.transport_category} onValueChange={(v) => setReqForm({ ...reqForm, transport_category: v })}>
                    <SelectTrigger data-testid="er-req-cat"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Adet</Label><Input type="number" min={1} value={reqForm.quantity} onChange={(e) => setReqForm({ ...reqForm, quantity: e.target.value })} /></div>
                <div><Label>Durum</Label>
                  <Select value={reqForm.running_condition} onValueChange={(v) => setReqForm({ ...reqForm, running_condition: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{CONDS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Marka / Model (ops.)</Label>
                <Input value={reqForm.make_model} onChange={(e) => setReqForm({ ...reqForm, make_model: e.target.value })} />
              </div>
              <div>
                <Label>En Geç Alım Tarih/Saat *</Label>
                <Input type="datetime-local" value={reqForm.deadline_at} onChange={(e) => setReqForm({ ...reqForm, deadline_at: e.target.value })} data-testid="er-req-deadline" />
              </div>
              <div>
                <Label>Açıklama (iletişim bilgisi yazmayın)</Label>
                <Textarea rows={2} value={reqForm.description} onChange={(e) => setReqForm({ ...reqForm, description: e.target.value })} data-testid="er-req-desc" />
              </div>
              <div className="flex gap-2 justify-end pt-2">
                <Button type="button" variant="outline" onClick={closeOfferReq} className="rounded-full">Vazgeç</Button>
                <Button type="submit" data-testid="er-req-submit" className="rounded-full bg-primary text-[hsl(var(--brand-black))] hover:bg-primary/90 font-bold">Teklif İsteğini Gönder</Button>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
